import { useNavigate } from 'react-router-dom';
import { SignupLayout } from './Layout.tsx';
import { useSignup } from './state.ts';

/**
 * Screen 3 — three "saying yes" checkboxes.
 *
 * Spec §11.3.3 + edtech-ux revision-3:
 *   - Three GRANULAR boxes (no all-in-one toggle)
 *   - Tone-checked copy reviewed with a Ukrainian K-2 educator
 *   - The word "consent" never appears in user-facing copy — we use
 *     "saying yes" / "I'm OK with..." instead, per spec
 *   - All three required to continue (no Dark Pattern; opt-in must
 *     be active for COPPA email-plus VPC)
 */
export function Step3Consent(): JSX.Element {
  const navigate = useNavigate();
  const { consents, setConsents } = useSignup();
  const allChecked = consents.parent && consents.save && consents.adult;

  return (
    <SignupLayout
      step={3}
      title="A few things to say yes to"
      subtitle="These help us follow the rules about kids and the internet. They're short."
    >
      <fieldset className="flex flex-col gap-3">
        <legend className="sr-only">Consents</legend>

        <CheckboxRow
          id="consent-parent"
          checked={consents.parent}
          onChange={(checked) => setConsents({ parent: checked })}
          label="I'm the parent or guardian of the child who will use this."
        />
        <CheckboxRow
          id="consent-save"
          checked={consents.save}
          onChange={(checked) => setConsents({ save: checked })}
          label="I'm OK with this app saving my child's designs and what they made (no ads, ever)."
        />
        <CheckboxRow
          id="consent-adult"
          checked={consents.adult}
          onChange={(checked) => setConsents({ adult: checked })}
          label="I'm at least 18 years old."
        />
      </fieldset>

      <button
        type="button"
        disabled={!allChecked}
        onClick={() => navigate('/signup/4')}
        className="mt-6 w-full rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Yes, continue
      </button>

      <p className="mt-4 text-xs text-slate-500">
        We&rsquo;ll send you an email to confirm. The app keeps working on this device for 24 hours
        while you check that.
      </p>
    </SignupLayout>
  );
}

interface CheckboxRowProps {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}

function CheckboxRow({ id, checked, onChange, label }: CheckboxRowProps): JSX.Element {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-3 rounded-2xl border-2 p-4 transition ${
        checked ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        // flex-shrink-0 prevents the checkbox from being squeezed when the
        // label wraps to multiple lines (the middle "saving designs" copy
        // is the longest of the three, which is what made it look smaller
        // on Mobile Safari before this).
        className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-slate-300 text-amber-500 focus:ring-amber-200"
      />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}
